import React from 'react';
import {
  CheckCircle2, Circle, Plane, Users, Star, Clock,
  Calendar, MapPin, Ticket, CreditCard
} from 'lucide-react';
import { journeySteps, flightDetails } from '../data/mockData';

const STEP_ICONS = {
  check: CheckCircle2,
  plane: Plane,
  users: Users,
  star: Star,
};

const STEP_COLORS = {
  done: { circle: 'bg-[#00C9B1] border-[#00C9B1]', text: 'text-[#00C9B1]', line: 'bg-[#00C9B1]' },
  active: { circle: 'bg-transparent border-[#F5A623]', text: 'text-[#F5A623]', line: 'bg-gradient-to-b from-[#F5A623] to-[#2D3748]' },
  pending: { circle: 'bg-transparent dark:border-[#2D3748] border-gray-300', text: 'dark:text-gray-500 text-gray-400', line: 'dark:bg-[#2D3748] bg-gray-200' },
};

export default function JourneyTracker() {
  const activeStep = journeySteps.find(s => s.status === 'active');
  const doneCount = journeySteps.filter(s => s.status === 'done').length;
  const progress = Math.round((doneCount / journeySteps.length) * 100);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold dark:text-white text-[#1A202C] mb-1">Journey Tracker</h2>
        <p className="text-sm dark:text-gray-400 text-gray-500">
          Pantau status perjalanan umroh Anda secara real-time
        </p>
      </div>

      {/* Progress Overview */}
      <div data-testid="journey-progress-card" className="rounded-2xl p-6 mb-6 dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0]">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs dark:text-gray-500 text-gray-400 mb-1">Paket Aktif</p>
            <p className="font-bold dark:text-white text-[#1A202C] text-lg">Paket Gold Ramadan 2026</p>
            <p className="text-sm dark:text-gray-400 text-gray-500">PT. Al-Barokah Tour & Travel</p>
          </div>
          <div className="text-center sm:text-right">
            <div className="text-3xl font-black text-[#00C9B1]">{progress}%</div>
            <div className="text-xs dark:text-gray-500 text-gray-400">Progress</div>
          </div>
        </div>
        <div className="w-full h-2 dark:bg-[#2D3748] bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#00C9B1] to-[#F5A623] transition-all duration-700"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs dark:text-gray-500 text-gray-400">
          <span>{doneCount} dari {journeySteps.length} tahap selesai</span>
          {activeStep && (
            <span className="text-[#F5A623] font-medium">Tahap Aktif: {activeStep.label}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Stepper - 3 cols */}
        <div data-testid="journey-stepper" className="lg:col-span-3 rounded-2xl p-6 dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0]">
          <h3 className="font-bold dark:text-white text-[#1A202C] mb-6">Status Perjalanan</h3>

          <div className="space-y-0">
            {journeySteps.map((step, i) => {
              const colors = STEP_COLORS[step.status];
              const Icon = STEP_ICONS[step.icon] || Circle;
              const isLast = i === journeySteps.length - 1;

              return (
                <div
                  key={step.id}
                  data-testid={`step-${step.id}`}
                  className="relative flex gap-4 pb-6"
                >
                  {/* Vertical line */}
                  {!isLast && (
                    <div
                      className={`absolute left-5 top-10 w-0.5 h-full ${step.status === 'done' ? 'bg-[#00C9B1]' : step.status === 'active' ? 'bg-gradient-to-b from-[#F5A623] to-[#2D3748]' : 'dark:bg-[#2D3748] bg-gray-200'}`}
                      style={{ top: '40px', height: 'calc(100% - 8px)' }}
                    />
                  )}

                  {/* Circle */}
                  <div className={`relative z-10 w-10 h-10 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    step.status === 'done'
                      ? 'bg-[#00C9B1] border-[#00C9B1]'
                      : step.status === 'active'
                      ? 'bg-[#F5A623]/10 border-[#F5A623] pulse-glow'
                      : 'dark:bg-[#242430] bg-gray-50 dark:border-[#2D3748] border-gray-300'
                  }`}>
                    {step.status === 'done' ? (
                      <CheckCircle2 size={18} className="text-[#0E0E12]" />
                    ) : step.status === 'active' ? (
                      <Icon size={16} className="text-[#F5A623]" />
                    ) : (
                      <Circle size={16} className="dark:text-[#2D3748] text-gray-300" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-1.5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`font-semibold text-sm ${
                          step.status === 'done'
                            ? 'text-[#00C9B1]'
                            : step.status === 'active'
                            ? 'text-[#F5A623]'
                            : 'dark:text-gray-500 text-gray-400'
                        }`}>
                          {step.label}
                          {step.status === 'active' && (
                            <span className="ml-2 text-[10px] font-bold bg-[#F5A623] text-[#0E0E12] px-2 py-0.5 rounded-full">
                              AKTIF
                            </span>
                          )}
                        </p>
                        <p className={`text-xs mt-0.5 ${step.status === 'pending' ? 'dark:text-gray-600 text-gray-400' : 'dark:text-gray-400 text-gray-500'}`}>
                          {step.description}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-xs font-medium ${step.status === 'pending' ? 'dark:text-gray-600 text-gray-400' : 'dark:text-gray-400 text-gray-500'}`}>
                          {step.date}
                        </p>
                        <p className="text-[10px] dark:text-gray-600 text-gray-400 mt-0.5">
                          {step.status === 'done' ? 'Selesai' : step.status === 'active' ? 'Sedang Proses' : 'Menunggu'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Flight Details - 2 cols */}
        <div className="lg:col-span-2 space-y-4">
          {/* Flight Card */}
          <div data-testid="flight-details-card" className="rounded-2xl overflow-hidden dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0]">
            {/* Image */}
            <div className="relative h-32 overflow-hidden dark:bg-[#242430] bg-gray-100">
              <img
                src={flightDetails.image}
                alt="Flight"
                className="w-full h-full object-cover opacity-80"
                onError={(e) => { e.target.style.display = 'none' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A24] to-transparent" />
              <div className="absolute bottom-3 left-4">
                <p className="text-xs dark:text-gray-300 text-white/80 font-medium">{flightDetails.airline}</p>
                <p className="text-lg font-black text-white">{flightDetails.flightNumber}</p>
              </div>
              <span className="absolute top-3 right-3 text-xs font-bold bg-[#00C9B1] text-[#0E0E12] px-2 py-1 rounded-full">
                {flightDetails.class}
              </span>
            </div>

            <div className="p-5">
              {/* Route */}
              <div className="flex items-center justify-between mb-5">
                <div className="text-center">
                  <p className="text-2xl font-black dark:text-white text-[#1A202C]">{flightDetails.origin.code}</p>
                  <p className="text-xs dark:text-gray-400 text-gray-500">{flightDetails.origin.city}</p>
                  <p className="text-xs font-semibold dark:text-gray-300 text-[#1A202C] mt-1">{flightDetails.departure.time}</p>
                </div>
                <div className="flex-1 px-3 flex flex-col items-center">
                  <div className="text-xs dark:text-gray-500 text-gray-400 mb-1">{flightDetails.duration}</div>
                  <div className="w-full flex items-center gap-1">
                    <div className="flex-1 h-px dark:bg-[#2D3748] bg-gray-200" />
                    <Plane size={14} className="text-[#00C9B1]" />
                    <div className="flex-1 h-px dark:bg-[#2D3748] bg-gray-200" />
                  </div>
                  <div className="text-[10px] dark:text-gray-600 text-gray-400 mt-1">Nonstop</div>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-black dark:text-white text-[#1A202C]">{flightDetails.destination.code}</p>
                  <p className="text-xs dark:text-gray-400 text-gray-500">{flightDetails.destination.city}</p>
                  <p className="text-xs font-semibold dark:text-gray-300 text-[#1A202C] mt-1">{flightDetails.arrival.time}</p>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Calendar, label: 'Tanggal', value: flightDetails.departure.date },
                  { icon: Ticket, label: 'Kursi', value: flightDetails.seat },
                  { icon: CreditCard, label: 'Kode Booking', value: flightDetails.bookingCode },
                  { icon: MapPin, label: 'Terminal', value: flightDetails.origin.terminal },
                ].map(item => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="dark:bg-[#242430] bg-[#F7FAFC] rounded-xl p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Icon size={12} className="text-[#00C9B1]" />
                        <span className="text-[10px] dark:text-gray-500 text-gray-400 uppercase tracking-wider">{item.label}</span>
                      </div>
                      <p className="text-xs font-semibold dark:text-gray-200 text-[#2D3748] truncate">{item.value}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Download E-Ticket */}
          <button
            data-testid="download-ticket-btn"
            className="w-full py-3 rounded-2xl border border-[#00C9B1]/30 text-[#00C9B1] font-semibold text-sm hover:bg-[#00C9B1]/10 transition-all flex items-center justify-center gap-2"
          >
            <Ticket size={16} />
            Download E-Tiket
          </button>
        </div>
      </div>
    </div>
  );
}
